import React, { useEffect, useState, useCallback } from "react";
import { UploadOutlined, DownloadOutlined, DeleteOutlined, EyeOutlined, CopyOutlined, } from "@ant-design/icons";
import { Upload, message, Tooltip } from "antd";
import { guardarSoportePP, getSoportesPP, deleteSoportePP, downloadSupportPP, viewSupportPP, } from "../../services/planesPedagogicos/planesPedagogicosServicesSoportes";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import config from "../../config";

const baseUrl = config.baseUrl;
const DOCUMENTOS_BASE = baseUrl + "/DocumentosFAO/DocumentosPOA";

const PlanPedagogicoSoporte = ({ dataPPSoporte, onUpload, onDelete }) => {
  const [soportes, setSoportes] = useState([]);

  const usernameFromState = useSelector((state) => state.auth.user);
  const appName = "mer";
  const permissions = useSelector((state) =>
    selectAppPermissions(state, appName)
  );

  const permissionDelete = permissions?.priv_delete === "Y";
  const permissionInsert = permissions?.priv_insert === "Y";

  const buildPublicUrl = (ruta) => {
    if (!ruta) return "";
    return `${DOCUMENTOS_BASE}${encodeURI(ruta)}`;
  };


  const fetchSoportes = useCallback(async () => {
    try {
      const { status, data } = await getSoportesPP(dataPPSoporte.oid);
      if (status === 200 && Array.isArray(data)) {
        setSoportes(data);
      } else {
        setSoportes([]);
      }
    } catch {
      message.error("Error al obtener los soportes MER");
    }
  }, [dataPPSoporte.oid]);

  useEffect(() => {
    fetchSoportes();
  }, [fetchSoportes]);

  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      if (!file || !dataPPSoporte?.name || !usernameFromState?.username) {
        message.error("Faltan datos para realizar la carga");
        return;
      }

      const ruta = `/actividad_22/tarea_221/indicador_32/medio_verificacion_27/Planes Pedagógicos/${dataPPSoporte.name}`;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("nombre", file.name);
      formData.append("ruta", ruta);
      formData.append("usuario", usernameFromState.username);
      formData.append("tipo_archivo", file.type);
      formData.append("oidProyecto", dataPPSoporte.oid);
      formData.append("activo", 1);

      const { status } = await guardarSoportePP(formData);

      if (status === 200) {
        message.success("Archivo subido correctamente");
        onSuccess(null, file);
        onUpload(dataPPSoporte);
        fetchSoportes();
      } else {
        onError(new Error("Error al subir el archivo"));
      }
    } catch {
      message.error("Error al subir el archivo");
    }
  };

  const handleRemove = async (file) => {
    try {
      const { status } = await deleteSoportePP(file.oid);
      if (status === 200) {
        message.success("Archivo eliminado correctamente");
        onDelete(dataPPSoporte.oid);
        fetchSoportes();
      }
    } catch {
      message.error("Error al eliminar el archivo");
    }
  };

  const handleDownload = async (file) => {
    try {
      const { status } = await downloadSupportPP(file.oid, file.name);
      if (status === 200) {
        message.success("Archivo descargado correctamente");
      }
    } catch {
      message.error("Error al descargar el archivo");
    }
  };

  const handlePreview = async (file) => {
    try {
      const extension = file.name.split(".").pop().toLowerCase();
      const allowedTypes = ["png", "jpeg", "jpg", "pdf"];

      if (allowedTypes.includes(extension)) {
        const { status } = await viewSupportPP(file.oid);
        if (status !== 200) {
          message.error("Error al abrir el archivo");
        }
      } else {
        message.info("No se puede visualizar, se descargará");
        handleDownload(file);
      }
    } catch {
      message.error("Error al abrir el archivo");
    }
  };

  const handleCopyLink = async (file) => {
    try {
      const soporte = soportes.find(
        (s) => s.oid.toString() === file.oid
      );

      if (!soporte?.ruta) {
        message.error("No se encontró la ruta del archivo");
        return;
      }

      const link = buildPublicUrl(soporte.ruta);

      await navigator.clipboard.writeText(link);
      message.success("Link copiado al portapapeles");
    } catch {
      message.error("No se pudo copiar el link");
    }
  };

  return (
    <div>
      {permissionInsert && (
        <div
          style={{
            border: "2px dashed #d9d9d9",
            padding: 8,
            borderRadius: "10px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100px",
          }}
        >
          <Upload
            customRequest={customRequest}
            showUploadList={false}
            listType="picture-card"
          >
            <div style={{ textAlign: "center" }}>
              <UploadOutlined style={{ fontSize: 24 }} />
              <div style={{ marginTop: 8 }}>Cargar Archivo</div>
            </div>
          </Upload>
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <Upload
          listType="picture"
          fileList={soportes.map((s) => ({
            oid: s.oid.toString(),
            name: s.nombre,
            status: "done",
          }))}
          onRemove={handleRemove}
          onPreview={handlePreview}
          onDownload={handleDownload}
          showUploadList={{
            showPreviewIcon: true,
            previewIcon: (
              <EyeOutlined style={{ fontSize: 20, color: "#1890ff" }} />
            ),
            showDownloadIcon: true,
            downloadIcon: (file) => (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  paddingRight: 10,
                }}
              >
                <DownloadOutlined
                  style={{ fontSize: 20, color: "#1890ff" }}
                />
                <Tooltip title="Copiar link">
                  <CopyOutlined
                    style={{
                      fontSize: 18,
                      color: "#1890ff",
                      cursor: "pointer",
                      paddingRight: 35,
                      paddingTop: 3,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyLink(file);
                    }}
                  />
                </Tooltip>
              </span>
            ),
            showRemoveIcon: permissionDelete,
            removeIcon: (
              <DeleteOutlined style={{ fontSize: 20, color: "red" }} />
            ),
          }}
        />
      </div>
    </div>
  );
};

export default PlanPedagogicoSoporte;
